import React, { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  KiteAlert,
  KiteIcon,
  KiteLink,
  KiteMenu,
  KiteMenuItem,
  KiteTooltip,
} from '@kite/react-kite'
import { copyToClipBoard, recordsAfterPagingAndSorting } from '../../../utils/utils'
import CustomTable, { Spacing } from '../../../components/table/Table'
import type { AppDispatch, RootState } from '../../../redux/Store'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchData,
  setError,
  updateFilters,
  updatePagination,
  updateSort,
} from '../../../redux/Sources/SourcesSlice'
import CustomToast from '../../../components/toast/CustomToast'
import ExtendIngestDays from './ExtendIngestDays/ExtendIngestDays'
import { ISourcesData } from '../../../redux/Sources/SourcesModels'
import { PATHS } from '../../../routes/Routes'

interface ITableProps {
  spacing: Spacing
}

const SourcesTable = ({ spacing = Spacing.Default }: ITableProps) => {
  const isInitialMount = useRef(true)
  const [searchParams] = useSearchParams()
  const history = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const [toastStatus, setToastStatus] = React.useState(false)
  const [extendIngestDaysSource, setExtendIngestDaysSource] = React.useState<ISourcesData | null>(
    null,
  )
  const [toastMsg, setToastMsg] = React.useState('')
  const {
    sourcesData = [],
    error,
    status,
    tableFilters,
    tablePagination,
    tablePagination: { PageNumber, RecordsPerPage },
    tableSort,
    tableSort: { SortBy, SortOrder },
    updateStatus,
  } = useSelector((state: RootState) => state.sourcesData)

  const columns = [
    {
      label: 'ID',
      sortKey: 'SourceID',
      accessorKey: 'SourceID',
      fixed: 'left',
      width: '10px',
    },
    {
      label: 'Source Title',
      sortKey: 'SourceTitle',
      accessorKey: 'SourceTitle',
      width: '120px',
    },
    {
      label: 'Source Path',
      sortKey: 'SourcePath',
      accessorKey: 'SourcePath',
      width: '380px',
      render: (row: any) => {
        const handleCopy = () => {
          copyToClipBoard(row?.SourcePath)
          setToastStatus(true)
          setToastMsg('Copied Source Path')
        }
        return (
          <KiteTooltip
            className='cell-file-path'
            type='truncation'
            id='kt1'
            text='Click to copy'
            direction='bottom'
          >
            <div className='cell-file-path' onClick={handleCopy}>
              <span>{row?.SourcePath}</span>
              <span className='cell-icon'>
                <KiteIcon icon='ki-copy' />
              </span>
            </div>
          </KiteTooltip>
        )
      },
    },
    {
      label: 'XGL',
      sortKey: 'EclipseSystem',
      accessorKey: 'EclipseSystem',
      width: '60px',
    },
    {
      label: 'Importing Month Days',
      sortKey: 'ImportFileFilter',
      accessorKey: 'ImportFileFilter',
      width: '180px',
    },
    {
      label: 'Enabled',
      sortKey: 'Enabled',
      accessorKey: 'Enabled',
      width: '40px',
      render: (row: any) => {
        return <div>{row?.Enabled ? 'True' : 'False'}</div>
      },
    },
    {
      label: 'Total',
      sortKey: 'ConfigurationCount',
      accessorKey: 'ConfigurationCount',
      width: '30px',
      render: (row: any) => {
        return (
          <KiteLink href={PATHS.VIEW_CONFIGURATIONS + '?SourceID=' + row?.SourceID}>
            {row?.ConfigurationCount}
          </KiteLink>
        )
      },
    },
    {
      label: 'Actions',
      sortKey: 'actions',
      fixed: 'right',
      accessorKey: 'actions',
      width: '20px',
      render: (row: any) => {
        const handleSelect = (key: string) => () => {
          if (key === 'Edit') {
            history(PATHS.EDIT_SOURCE + '/' + row?.SourceID, { state: row })
          }
          if (key === 'Delete') {
            history(PATHS.DELETE_SOURCE + '/' + row?.SourceID, { state: row })
          }
          if (key === 'ExtendIngestDays') {
            setExtendIngestDaysSource(row)
          }
          if (key === 'Activity') {
            history(PATHS.BATCH_ACTIVITY + '?SourceID=' + row?.SourceID)
          }
          if (key === 'Details') {
            history(
              PATHS.SOURCE_DETAILS + '?SourceID=' + row?.SourceID + '&FileType=' + row?.FileType,
            )
          }
        }
        return (
          <div>
            <KiteMenu variant='flow'>
              <KiteMenuItem onSelect={handleSelect('Edit')}>
                <span className='flex-center'>
                  <KiteIcon className='copy-icon' icon='ki-edit' />
                  Edit Source
                </span>
              </KiteMenuItem>
              <KiteMenuItem onSelect={handleSelect('Delete')}>
                <span className='flex-center'>
                  <KiteIcon className='copy-icon' icon='ki-trash' />
                  Delete Source
                </span>
              </KiteMenuItem>
              <KiteMenuItem onSelect={handleSelect('Details')}>
                <span className='flex-center'>
                  <KiteIcon className='copy-icon' icon='ki-folder' />
                  Source Details
                </span>
              </KiteMenuItem>
              <KiteMenuItem onSelect={handleSelect('Activity')}>
                <span className='flex-center'>
                  <KiteIcon className='copy-icon' icon='ki-activity' />
                  Activity
                </span>
              </KiteMenuItem>
              <KiteMenuItem onSelect={handleSelect('ExtendIngestDays')}>
                <span className='flex-center'>
                  <KiteIcon className='copy-icon' icon='ki-calendar' />
                  Extend Ingest Days
                </span>
              </KiteMenuItem>
            </KiteMenu>
          </div>
        )
      },
    },
  ]

  const handleSort = (column: string) => {
    const currentSortKey = tableSort?.SortBy
    const sortDirection = tableSort?.SortOrder
    let newSortDirection: any = 'asc'
    if (currentSortKey === column) {
      if (!sortDirection) {
        newSortDirection = 'asc'
      } else if (sortDirection === 'asc') {
        newSortDirection = 'desc'
      } else if (sortDirection === 'desc') {
        newSortDirection = 'asc'
      }
    } else {
      newSortDirection = 'asc'
    }
    dispatch(updateSort({ SortBy: column, SortOrder: newSortDirection }))
  }

  useEffect(() => {
    // Default values
    const currentFiltersData: { [x: string]: any } = { ...tableFilters }
    const currentSortData: { [x: string]: any } = { ...tableSort }
    const currentPaginationData: { [x: string]: any } = { ...tablePagination }
    // Values From URl
    const paramEntries: any = searchParams.entries()
    for (const entry of paramEntries) {
      const [param, value] = entry
      if (value) {
        if (param === 'PageNumber' || param === 'RecordsPerPage') {
          currentPaginationData[param] = value
        } else if (param === 'SortBy' || param === 'SortOrder') {
          currentSortData[param] = value
        } else {
          currentFiltersData[param] = value
        }
      }
    }
    dispatch(updateFilters({ ...currentFiltersData }))
    dispatch(updateSort({ ...currentSortData }))
    dispatch(updatePagination({ ...currentPaginationData }))
  }, [])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    const reqPayload: { [x: string]: any } = {
      ...tableFilters,
      ...tablePagination,
      ...tableSort,
    }
    // Setting applied values to URL
    const params = new URLSearchParams()
    Object.keys(reqPayload).forEach((filterKey) => {
      if (reqPayload[filterKey]) {
        params.append(filterKey, reqPayload[filterKey])
      }
    })
    history({ search: params.toString() }, { replace: true })
    dispatch(
      fetchData(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        reqPayload,
      ),
    )
  }, [
    JSON.stringify({
      ...tableFilters,
      ...tablePagination,
      ...tableSort,
    }),
    isInitialMount.current,
  ])

  useEffect(() => {
    if (updateStatus === 'succeeded') {
      dispatch(
        fetchData(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          {
            ...tableFilters,
            ...tablePagination,
            ...tableSort,
          },
        ),
      )
    }
  }, [updateStatus])

  const handleClearError = () => {
    dispatch(setError(''))
  }

  const handlePagination = (name: string) => (value: number) => {
    dispatch(
      updatePagination({
        ...tablePagination,
        [name]: value,
        ...(name === 'RecordsPerPage' && { PageNumber: 1 }),
      }),
    )
  }

  const handleToastExpire = () => {
    setToastStatus(false)
    setToastMsg('')
  }

  const handleExtendIngestDaysDialog = () => {
    setExtendIngestDaysSource(null)
  }

  const totalItems = Number(sourcesData[0]?.MaxCount || 0)
  const isLoading = status === 'loading'
  const paginatedRecords = recordsAfterPagingAndSorting(
    sourcesData,
    SortOrder,
    SortBy,
    PageNumber,
    RecordsPerPage,
  )
  return (
    <>
      {error && (
        <div className='alert-element'>
          <KiteAlert
            onClose={handleClearError}
            autoFocus={false}
            level='page'
            type='error'
            description={error}
            dismissible
          />
        </div>
      )}
      <CustomTable
        columns={columns}
        currentSortKey={tableSort?.SortBy}
        sortDirection={tableSort?.SortOrder}
        onSort={handleSort}
        data={paginatedRecords || []}
        spacing={spacing}
        loading={isLoading}
        currentPage={PageNumber}
        onPageChange={handlePagination('PageNumber')}
        itemsPerPage={RecordsPerPage}
        onItemsPerPageChange={handlePagination('RecordsPerPage')}
        totalItems={totalItems}
      />
      <CustomToast status={toastStatus} message={toastMsg} onExpire={handleToastExpire} />
      <ExtendIngestDays
        status={!!extendIngestDaysSource}
        data={extendIngestDaysSource}
        onClose={handleExtendIngestDaysDialog}
      />
    </>
  )
}

export default SourcesTable
